import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { AvatarGroup } from "components/DesignSystem/AvatarGroup/AvatarGroup";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Table from "components/DesignSystem/Table/V2/Table";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFilters } from "hooks/useFilter";
import {
  RuleEngineFilter,
  RuleEngineFilterType,
} from "components/RuleEngine/RuleEngineFilter/RuleEngineFilter";
import { RULE_SOURCE, RULE_STATE } from "constants/ruleEngine";
import {
  CellContext,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import EmptyRuleEngine from "static/images/EmptyRuleEngine.svg";
import ThreeDots from "static/images/ThreeDots.svg";
import {
  useGetAllRulesQuery,
  useMarkRuleActiveMutation,
  useCreateRuleMutation,
} from "store/apis/ruleEngine";
import {
  openDeleteModal,
  openEditModal,
  openSlider,
  setAllRuleIds,
} from "store/slices/ruleEngine";
import { useToast } from "hooks/useToast";
import { RootState } from "store/store";
import { RulesData, ValueSupported } from "types/Models/ruleEngine";
import { debounce } from "utils/debouncing";
import { CreateRule } from "./CreateRule";
import { DeleteRule } from "./DeleteRule";
import { EditRuleModal } from "./EditRuleModal";
import { RuleSlider } from "./RuleSlider";
import { MobileRuleRow } from "./MobileRuleRow";
import { useWindowSize } from "hooks/useWindowSize";
import { useGetAllTransactionMerchantsQuery } from "store/apis/merchant";
import { useGetAllAccountQuery } from "store/apis/transactions";
import { Tag } from "components/DesignSystem/Tag/Tag";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { useQuery } from "hooks/useQuery";
import { DisabledActionsSection } from "components/Transaction/ActionDropdown";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { UserGroup } from "components/icons/UserGroup";
import { MagicWand } from "components/icons/MagicWand";
import { TRANSACTION_COLUMNS } from "constants/transaction";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { BackendError } from "types/utils/error";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { Padlock } from "components/icons/Padlock";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

export const VendorInfo = ({
  value,
  tag = false,
}: {
  value: string[];
  tag?: boolean;
}) => {
  const groupId = useCurrentGroupContext().uuid;
  const entityId = useCurrentEntityId();

  const merchantIds = value.join(",");
  const { data: merchants, isLoading } = useGetAllTransactionMerchantsQuery(
    {
      merchantIds,
      groupId,
      entityId,
    },
    { skip: !groupId || !entityId || !value.length }
  );

  if (isLoading) {
    return <Loader size="small" />;
  }

  if (!merchants) {
    return null;
  }

  if (tag) {
    return (
      <div className="t-inline-flex t-gap-1 t-flex-wrap">
        {merchants.merchants.map(({ name }) => (
          <Tag icon={false} key={name} tagType="gray">
            {name}
          </Tag>
        ))}
      </div>
    );
  }

  return merchants.merchants.map(({ name }) => name).join(" or ");
};

export const SourceInfo = ({
  value,
  tag = false,
}: {
  value: string[];
  tag?: boolean;
}) => {
  const groupId = useCurrentGroupContext().uuid;
  const entityId = useCurrentEntityId();
  const { data: banks = [], isLoading } = useGetAllAccountQuery(
    {
      groupId,
      entityId,
      account_type: "EXCLUDE_STRIPE",
    },
    { skip: !groupId || !entityId }
  );

  if (isLoading) {
    return <Loader size="small" />;
  }

  if (tag) {
    return (
      <div className="t-inline-flex t-gap-1 t-flex-wrap">
        {value
          .map((v) => banks.find((b) => b.uuid === v))
          .map((v) => (
            <Tag icon={false} key={v?.uuid} tagType="gray">
              {v?.nickname}
            </Tag>
          ))}
      </div>
    );
  }

  return value
    .map((v) => banks.find((b) => b.uuid === v))
    .map((v) => v?.nickname)
    .join(" or ");
};

export const ValueType = ({
  value_type,
  value,
  tag = false,
}: {
  value_type: ValueSupported;
  value: string | number | string[];
  tag?: boolean;
}) => {
  switch (value_type) {
    case "CHOICE": {
      return <> {value}</>;
    }

    case "DECIMAL": {
      return (
        <>
          {" "}
          <AmountSuperScript amount={Number(value)} />
        </>
      );
    }

    case "TEXT": {
      return <> {value}</>;
    }

    case "VENDOR": {
      return value instanceof Array ? (
        <VendorInfo tag={tag} value={value} />
      ) : null;
    }

    case "SOURCE": {
      return value instanceof Array ? (
        <SourceInfo tag={tag} value={value} />
      ) : null;
    }

    default:
      return <> {value}</>;
  }
};

const ConditionsColumn = (
  info: CellContext<RulesData, RulesData["conditions"]>
) => {
  const conditions = info.getValue();

  if (!conditions || conditions.length === 0) {
    return null;
  }

  const { condition_operand, condition_operator, value, value_type, uuid } =
    conditions[0];

  return (
    <div className="t-flex t-gap-1.5">
      <div className="t-gap-1 t-flex t-bg-neutral-0 t-rounded t-py-1 t-px-2 t-text-subtitle-sm t-text-text-60 sm:t-items-center sm:t-py-0.5 sm:t-px-1.5 sm:t-text-subtext-sm">
        <span className="t-text-nowrap">{condition_operand.name}</span>
        <span className="t-text-nowrap t-text-body-sm t-text-text-30 sm:t-text-caption">
          {condition_operator.name}
        </span>
        <span className="t-line-clamp-1">
          {Array.isArray(value) ? (
            <ToolTip
              text={<ValueType tag value={value} value_type={value_type} />}
            >
              <span>
                <ValueType value={value} value_type={value_type} />
              </span>
            </ToolTip>
          ) : (
            <ValueType value={value} value_type={value_type} />
          )}
        </span>
      </div>
      {conditions.length > 1 && (
        <ToolTip text={`${conditions.length - 1} more condition`}>
          <div className="t-gap-1 t-text-nowrap t-flex t-bg-neutral-0 t-rounded t-py-1 t-px-2 t-text-text-60 sm:t-py-0.5 sm:t-px-1.5 sm:t-text-body-sm">
            + {conditions.length - 1}
          </div>
        </ToolTip>
      )}
    </div>
  );
};

export const Actions = ({
  rule,
  hasWritePermission,
}: {
  rule: RulesData;
  hasWritePermission: boolean;
}) => {
  const dispatch = useDispatch();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const [markRuleActive] = useMarkRuleActiveMutation();
  const [createRule] = useCreateRuleMutation();

  const onEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(openEditModal(rule.uuid));
  };

  const onDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(openDeleteModal(rule.uuid));
  };

  const onDuplicate = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const rule_condition_data = rule.conditions.map(
        ({ condition_operand, condition_operator, value }) => ({
          operand_id: condition_operand.uuid,
          operator_id: condition_operator.uuid,
          value,
        })
      );

      const payload = {
        entityId,
        groupId,
        name: rule.name,
        rule_start_date: rule.start_date || undefined,
        rule_end_date: rule.end_date || undefined,
        assigned_category_id: rule.category?.uuid || "",
        apply_for_txns_with_no_category: rule.apply_to_txns_with_no_category,
        apply_for_future_txns: rule.apply_to_future_txns,
        apply_on_all_past_txns: rule.apply_on_all_past_txns,
        rule_condition_data,
        assigned_classfication_ids: rule.classification_data?.map(
          (cls) => cls.uuid
        ),
        assigned_merchant_id: rule.merchant_data?.uuid || "",
        priority: rule.priority,
        is_active: true,
      };

      await createRule(payload).unwrap();

      successToast({ message: "Rule duplicated successfully" });
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to duplicate rule",
      });
    }
  };

  const onDisable = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await markRuleActive({
        entityId,
        groupId,
        ruleId: rule.uuid,
        is_active: false,
      }).unwrap();

      successToast({ message: "Rule disabled successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const onEnable = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      await markRuleActive({
        entityId,
        groupId,
        ruleId: rule.uuid,
        is_active: true,
      }).unwrap();

      successToast({ message: "Rule enabled successfully" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const isSystemGenerated = Boolean(rule.source === RULE_SOURCE.SYSTEM);
  const isDisabled = !rule.is_active;

  const enabled: React.ReactNode[] = [];
  const disabled: React.ReactNode[] = [];

  if (!isDisabled) {
    enabled.push(
      <Dropdown.Item key="duplicate" onClick={onDuplicate}>
        Duplicate rule
      </Dropdown.Item>
    );
  }

  if (!isSystemGenerated && isDisabled) {
    enabled.push(
      <Dropdown.Item key="enable" onClick={onEnable}>
        Enable rule
      </Dropdown.Item>,
      <Dropdown.Item key="delete" onClick={onDelete}>
        <p className="t-m-0 t-text-red">Delete rule</p>
      </Dropdown.Item>
    );
  }

  if (!isSystemGenerated && !isDisabled) {
    enabled.push(
      <Dropdown.Item key="disable" onClick={onDisable}>
        Disable rule
      </Dropdown.Item>
    );
    enabled.push(
      <Dropdown.Item key="edit" onClick={onEdit}>
        Edit rule
      </Dropdown.Item>
    );
    enabled.push(
      <Dropdown.Item key="delete" onClick={onDelete}>
        <p className="t-m-0 t-text-red">Delete rule</p>
      </Dropdown.Item>
    );
  }

  if (isSystemGenerated) {
    disabled.push(
      <Dropdown.Item key="disable" disabled>
        Disable rule
      </Dropdown.Item>
    );
    disabled.push(
      <Dropdown.Item key="edit" disabled>
        Edit rule
      </Dropdown.Item>
    );
    disabled.push(
      <Dropdown.Item key="delete" disabled>
        Delete rule
      </Dropdown.Item>
    );
  }

  if (!hasWritePermission) {
    return (
      <div
        className="t-pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        <Button size="small" customType="ghost_icon" disabled>
          <Padlock />
        </Button>
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dropdown.Root>
        <Dropdown.Trigger asChild>
          <span>
            <Button size="xsmall" customType="ghost_icon">
              <img src={ThreeDots} alt="Action" className="t-select-none" />
            </Button>
          </span>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content
            sideOffset={8}
            side="bottom"
            align="end"
            className={classNames({ "!t-w-60": isSystemGenerated })}
          >
            {enabled}
            {disabled.length > 0 && (
              <DisabledActionsSection
                disabledItems={disabled}
                tooltipMessage="Contact Inkle Support to update the system rules."
              />
            )}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
    </div>
  );
};

export const RuleEngine = () => {
  usePageTitle("Rules");
  const { uuid: groupId } = useCurrentGroupContext();
  const query = useQuery();
  const entityId = useCurrentEntityId();
  const [searchTerm, setSearchTerm] = useState("");
  const dispatch = useDispatch();
  const createColumn = createColumnHelper<RulesData>();
  const selectedRuleId = query.get("rule_id");
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );

  const { values: filter, updateFilter } = useFilters<RuleEngineFilterType>({
    initialValue: {
      STATE: null,
      RULE_SOURCE: null,
    },
    useQueryParams: true,
  });

  useEffect(() => {
    if (selectedRuleId) {
      dispatch(openSlider(selectedRuleId));
    }
  }, [dispatch, selectedRuleId]);

  const { previewRuleId } = useSelector((store: RootState) => store.ruleEngine);

  const [pageNum, setPageNum] = useState(1);

  const { data, isLoading, isSuccess, isFetching } = useGetAllRulesQuery(
    {
      entityId,
      groupId,
      pageNum,
      searchTerm,
      state: filter.STATE,
      source: filter.RULE_SOURCE,
    },
    { skip: !groupId || !entityId }
  );

  const {
    rules_data = [],
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = data || {};

  const isEmpty = rules_data.length === 0;

  const { close, isOpen, open } = useModal();

  const handleSearch = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value);
  });

  const columns = [
    createColumn.accessor("name", {
      size: 30,
      header: "title",
      cell: (info) => {
        const name = info.getValue();
        return <>{name}</>;
      },
    }),

    createColumn.accessor("conditions", {
      size: 30,
      header: "conditions",
      cell: ConditionsColumn,
    }),

    createColumn.accessor("category", {
      size: 25,
      header: "category",
      cell: (info) => {
        const category = info.getValue();
        return <>{category?.name || "-"}</>;
      },
    }),

    createColumn.accessor("source", {
      size: 20,
      header: () => <HeadCellContent title="Type" align="center" />,
      cell: (info) => {
        const isSystemGenerated = Boolean(
          info.getValue() === RULE_SOURCE.SYSTEM
        );
        return (
          <ToolTip
            text={isSystemGenerated ? "System generated" : "User generated"}
          >
            <div className="t-w-full t-flex t-justify-center">
              {isSystemGenerated ? (
                <div className="t-flex t-items-center t-justify-center t-size-7 t-bg-white t-rounded-full t-border t-border-solid t-border-neutral-0 t-text-neutral-40">
                  <MagicWand className="t-size-3" />
                </div>
              ) : (
                <UserGroup className="t-size-7" />
              )}
            </div>
          </ToolTip>
        );
      },
    }),

    createColumn.accessor("uuid", {
      id: TRANSACTION_COLUMNS.OPTIONS,
      size: 5,
      header: " ",
      cell: (info) => {
        const rule = info.row.original;
        return <Actions rule={rule} hasWritePermission={hasWritePermission} />;
      },
    }),
  ];

  const { isMobile } = useWindowSize();

  const table = useReactTable({
    data: rules_data || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  const openRuleSlider = (ruelId: string) => {
    const allRuleIds = rules_data.map(({ uuid }) => uuid);

    dispatch(openSlider(ruelId));
    dispatch(setAllRuleIds(allRuleIds));
  };

  return (
    <DashboardContainer className="t-relative t-h-full t-pr-6  t-pl-6 sm:t-px-4">
      <DashboardContainer.Header
        sticky
        className="t-flex t-flex-col t-gap-3 t-pb-4"
      >
        <div className="t-flex t-justify-between sm:t-gap-3">
          <div className="t-w-1/2 sm:t-w-auto sm:t-min-w-0 sm:t-flex-1">
            <Search
              isLoading={isFetching}
              block
              defaultValue={searchTerm}
              placeholder="Search"
              onChange={handleSearch}
            />
          </div>
          <LockedUi
            feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
            blockedUi={<LockedButton text="Create rule" />}
          >
            <Button customType="primary" size="small" onClick={open}>
              Create rule
            </Button>
          </LockedUi>
        </div>
        <div className="t-flex t-justify-between t-w-full sm:t-flex-wrap sm:t-justify-end sm:t-gap-2">
          <div className="sm:t-w-full">
            <RuleEngineFilter filter={filter} updateFilter={updateFilter} />
          </div>
          <PaginationHandler
            page_num={pageNum}
            per_page={per_page}
            total_count={total_count}
            total_pages={total_pages}
            onPageNumChange={setPageNum}
            isLoading={isFetching}
          />
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-pb-32">
        <Async.Root {...{ isLoading, isSuccess, isEmpty }}>
          <Async.Empty>
            <EmptyScreen
              text="Start creating rules"
              cta={
                <LockedUi
                  feature={WRITE_PERMISSION_KEYS.TRANSACTIONS}
                  blockedUi={<LockedButton text="Create rule" />}
                >
                  <Button customType="primary" size="small" onClick={open}>
                    Create rule
                  </Button>
                </LockedUi>
              }
            >
              <img src={EmptyRuleEngine} alt="No rules found" />
            </EmptyScreen>
          </Async.Empty>
          <Async.Success>
            {isMobile && (
              <div className="t-flex t-flex-col">
                {table.getRowModel().rows.map((row) => {
                  const isDisabled = !row.original.is_active;

                  return (
                    <MobileRuleRow
                      key={row.id}
                      row={row}
                      isDisabled={isDisabled}
                      isPreviewed={previewRuleId === row.original.uuid}
                      onClick={() =>
                        !isDisabled && openRuleSlider(row.original.uuid)
                      }
                    />
                  );
                })}
              </div>
            )}
            {!isMobile && (
              <TableUI table={table}>
                {({ row }) => {
                  const isDisabled = !row.original.is_active;

                  return (
                    <ConditionalToolTip
                      condition={
                        isDisabled &&
                        "This rule is disabled. Enable it from the menu to apply/edit."
                      }
                    >
                      <Table.Row
                        key={row.id}
                        onClick={() =>
                          !isDisabled && openRuleSlider(row.original.uuid)
                        }
                        className={classNames(
                          "hover:t-bg-surface-lighter-grey t-group",
                          {
                            "t-bg-surface-lighter-grey":
                              previewRuleId === row.original.uuid,

                            "t-text-text-100 t-cursor-pointer": !isDisabled,
                            "t-text-text-30": isDisabled,
                          }
                        )}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const isOptionsColumn =
                            cell.column.id === TRANSACTION_COLUMNS.OPTIONS;
                          return (
                            <Table.Cell
                              key={cell.id}
                              style={{ width: `${cell.column.getSize()}%` }}
                              className={classNames("t-py-4 t-px-2", {
                                "t-opacity-50": isDisabled && !isOptionsColumn,
                              })}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </Table.Cell>
                          );
                        })}
                      </Table.Row>
                    </ConditionalToolTip>
                  );
                }}
              </TableUI>
            )}
            <RuleSlider />
          </Async.Success>
        </Async.Root>
      </DashboardContainer.Content>
      {isOpen && <CreateRule isOpen={isOpen} close={close} />}
      <EditRuleModal />
      <DeleteRule />
    </DashboardContainer>
  );
};
